import { useCallback, useEffect, useRef, useState } from "react";

import { fetchTender, type TenderDetails } from "./api";

/**
 * The linked tender's record (title, summary, criteria, win themes) for the
 * Tender tab's overview, refetched when the bid changes and on demand. The
 * questions themselves come from useQuestions.
 */
export const useTender = (bidId: string | null, token: string | null) => {
  const [tender, setTender] = useState<TenderDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const refresh = useCallback(async () => {
    if (!bidId || !token) {
      setTender(null);
      return;
    }
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setError(null);
    try {
      const next = await fetchTender(bidId, token, controller.signal);
      if (!controller.signal.aborted) setTender(next);
    } catch (err) {
      if (controller.signal.aborted) return;
      setError(err instanceof Error ? err.message : "Could not load the tender");
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [bidId, token]);

  useEffect(() => {
    void refresh();
    return () => abortRef.current?.abort();
  }, [refresh]);

  return { tender, loading, error, refresh };
};
